import {
   emptyProvenance,
   getPageFindReason,
   pageFindConfidenceStatus,
   pagesDifferFromAi,
   provenanceAfterAiAnalysis,
   provenanceAfterContentAnalysis,
   provenanceAfterLocate,
   resolvePageFindStatus,
   statusFromConfidence,
   type ANNServitutProvenance,
   type ServitutAiFieldStatus,
   type ServitutPageFindStatus,
} from "src/modules/ANNModule/ANNServitutProvenance";
import { overlayAcceptedFieldStatuses } from "src/modules/ANNModule/ANNServitutFieldAnnotations";

export function isElectronicServitutRow(row: { handling?: string }): boolean {
   const handling = row.handling ?? "";
   return handling === "electronicZipPackage";
}

export function isCustomUploadRow(row: { documentSource?: string }): boolean {
   return row.documentSource === "customUpload";
}

export function getRowAnalysisItem(row: any): any | undefined {
   return row?.analysisItem;
}

export function getRowSelectedPages(row: any): number[] {
   return row?.selectedPages || [];
}

export function getRowDatoLoebenr(row: any): string {
   return row?.datoLoebenr || row?.displayAktId || "";
}

export function getRowHeading(row: any): string {
   return (
      row?.summary
      || getRowAnalysisItem(row)?.summary
      || row?.servitut?.tekstSummarisk
      || ""
   ).toString().trim();
}

export function getRowCopyText(row: any): string {
   return [getRowDatoLoebenr(row), getRowHeading(row)].filter(Boolean).join(" ");
}

export function getRowProvenance(row: any): ANNServitutProvenance {
   return row?.provenance
      || row?.annotationRecord?.document?.provenance
      || row?.annotationDocument?.provenance
      || emptyProvenance();
}

export function getRowPageFindStatus(row: any): ServitutPageFindStatus {
   if (isElectronicServitutRow(row)) {
      return isRowFetched(row) ? "found" : "not_run";
   }
   const analysis = getRowAnalysisItem(row);
   return resolvePageFindStatus({
      electronicDocument: false,
      hasAnalysis: Boolean(analysis),
      pagesKept: analysis?.pages_kept || [],
   });
}

export type ServitutDocumentExtent =
   | { kind: "missing" }
   | { kind: "entire_akt" };

export type ServitutPageSourceStatus =
   | { kind: "hidden" }
   | { kind: "not_run" }
   | { kind: "ai_none" }
   | { kind: "ai_pages"; count: number; confident: boolean; reason?: string }
   | { kind: "user_pages"; count: number };

export function getRowPageSourceStatus(row: any): ServitutPageSourceStatus {
   if (isElectronicServitutRow(row)) {
      return { kind: "hidden" };
   }
   const selected = getRowSelectedPages(row);
   const analysis = getRowAnalysisItem(row);
   const aiPages = analysis?.pages_kept || getRowProvenance(row).aiPagesKept || [];
   const hasAiPageSearch = Array.isArray(analysis?.pages_kept)
      || Boolean(getRowProvenance(row).aiPagesKept?.length);
   if (selected.length > 0 && (!hasAiPageSearch || pagesDifferFromAi(selected, aiPages, false))) {
      return { kind: "user_pages", count: selected.length };
   }
   if (!hasAiPageSearch) {
      return { kind: "not_run" };
   }
   if (!aiPages.length) {
      return { kind: "ai_none" };
   }
   const storedPagesStatus = getRowProvenance(row).fieldStatus?.pages;
   const pagesStatus = storedPagesStatus === "ai_glad" || storedPagesStatus === "ai_ikke_glad"
      ? storedPagesStatus
      : pageFindConfidenceStatus(analysis, aiPages);
   const reason = getPageFindReason(analysis);
   return {
      kind: "ai_pages",
      count: aiPages.length,
      confident: pagesStatus !== "ai_ikke_glad",
      reason,
   };
}

export function getRowDocumentExtent(row: any): ServitutDocumentExtent {
   if (!isRowFetched(row)) {
      return { kind: "missing" };
   }
   if (isElectronicServitutRow(row) || isCustomUploadRow(row)) {
      return { kind: "missing" };
   }
   const source = getRowPageSourceStatus(row);
   if (source.kind === "not_run") {
      return { kind: "entire_akt" };
   }
   return { kind: "missing" };
}

export function isRowFetched(row: any): boolean {
   const status = row?.pdfStatus;
   if (status === "ready" || row?.documentSource === "annotation") {
      return true;
   }
   if (isElectronicServitutRow(row) && status === "ready") {
      return true;
   }
   if (isCustomUploadRow(row) && row?.customPdf?.s3Key) {
      return true;
   }
   return false;
}

export function isRowControlled(row: any): boolean {
   const review = getRowProvenance(row).reviewState;
   return review === "checked" || review === "rejected";
}

export function isRowApproved(row: any): boolean {
   return getRowProvenance(row).reviewState === "checked";
}

export function isRowRejected(row: any): boolean {
   return getRowProvenance(row).reviewState === "rejected";
}

export function isImportedAnnotationPendingPageSearch(row: any): boolean {
   return row?.documentSource === "annotation" && getRowPageFindStatus(row) === "not_run";
}

export function hasAiPageSuggestion(row: any): boolean {
   const pages = getRowProvenance(row).aiPagesKept || getRowAnalysisItem(row)?.pages_kept || [];
   return pages.length > 0;
}

export function getRowPageApprovalPlan(row: any): {
   pagesToSave: number[];
   electronic: boolean;
   differs: boolean;
   canApprove: boolean;
} {
   const selected = getRowSelectedPages(row);
   const aiPages = getRowAnalysisItem(row)?.pages_kept || getRowProvenance(row).aiPagesKept || [];
   const pagesToSave = selected.length ? selected : aiPages;
   const electronic = isElectronicServitutRow(row);
   return {
      pagesToSave,
      electronic,
      differs: pagesDifferFromAi(pagesToSave, aiPages, electronic),
      canApprove: isRowFetched(row),
   };
}

export type ServitutPageControlTone = "approved" | "rejected" | "good" | "warn" | "bad" | "pending";

/** Phase A row color: person approve/reject overrides AI page confidence. */
export function getPageControlTone(row: any): ServitutPageControlTone {
   const review = getRowProvenance(row).reviewState;
   if (review === "checked") {
      return "approved";
   }
   if (review === "rejected") {
      return "rejected";
   }
   const pageSource = getRowPageSourceStatus(row);
   if (pageSource.kind === "user_pages") {
      return "pending";
   }
   if (pageSource.kind === "ai_none") {
      return "bad";
   }
   if (pageSource.kind === "ai_pages") {
      return pageSource.confident ? "good" : "warn";
   }
   return "pending";
}

function contentFieldStatuses(
   analysisItem: any | undefined,
   canRun: boolean,
   hasCaseDescription = true,
): {
   resumeStatus: ServitutAiFieldStatus;
   entitledPartyStatus: ServitutAiFieldStatus;
   conditionStatus: ServitutAiFieldStatus;
   statementStatus: ServitutAiFieldStatus;
} {
   return {
      resumeStatus: statusFromConfidence(
         analysisItem?.overall_confidence,
         false,
         Boolean(String(analysisItem?.summary || "").trim()),
         canRun,
      ),
      entitledPartyStatus: statusFromConfidence(
         analysisItem?.paataleberettiget_confidence,
         false,
         Boolean(analysisItem?.paataleberettiget || analysisItem?.paataleberettiget_resolution),
         canRun,
      ),
      conditionStatus: statusFromConfidence(
         analysisItem?.servitut_type_confidence,
         false,
         Boolean(analysisItem?.servitut_type),
         canRun,
      ),
      statementStatus: hasCaseDescription
         ? statusFromConfidence(
            analysisItem?.case_relevance_confidence,
            false,
            analysisItem?.case_relevant !== undefined || Boolean(analysisItem?.case_relevance_reasoning),
            canRun,
         )
         : (canRun ? "not_run" : "cannot_run"),
   };
}

export function getContentFieldAiStatus(
   row: any,
   field: "resume" | "entitledParty" | "condition" | "statement",
   options?: { hasCaseDescription?: boolean },
): ServitutAiFieldStatus {
   const statuses = contentFieldStatuses(
      row?.analysisItem,
      isRowFetched(row),
      options?.hasCaseDescription !== false,
   );
   if (field === "entitledParty") {
      return statuses.entitledPartyStatus;
   }
   if (field === "condition") {
      return statuses.conditionStatus;
   }
   if (field === "statement") {
      return statuses.statementStatus;
   }
   return statuses.resumeStatus;
}

export function buildAiProvenanceForAnalysisItem(
   row: any,
   analysisItem: any | undefined,
   options?: { hasCaseDescription?: boolean },
): ANNServitutProvenance {
   const previous = getRowProvenance(row);
   const electronic = isElectronicServitutRow(row);
   const canRun = isRowFetched(row);
   const pagesKept = analysisItem?.pages_kept || [];
   const pageFind = resolvePageFindStatus({
      electronicDocument: electronic,
      hasAnalysis: Boolean(analysisItem),
      pagesKept,
   });
   const fieldStatuses = contentFieldStatuses(analysisItem, canRun, options?.hasCaseDescription !== false);
   return overlayAcceptedFieldStatuses(provenanceAfterAiAnalysis(previous, {
      aiPagesKept: pagesKept,
      pageFindStatus: pageFind,
      resumeStatus: fieldStatuses.resumeStatus,
      entitledPartyStatus: fieldStatuses.entitledPartyStatus,
      conditionStatus: fieldStatuses.conditionStatus,
      statementStatus: fieldStatuses.statementStatus,
   }), row?.fieldAnnotations);
}

export function buildLocateProvenanceForAnalysisItem(
   row: any,
   analysisItem: any | undefined,
   options?: { analysisStale?: boolean },
): ANNServitutProvenance {
   const previous = getRowProvenance(row);
   const electronic = isElectronicServitutRow(row);
   const pagesKept = analysisItem?.pages_kept || [];
   const pageFind = resolvePageFindStatus({
      electronicDocument: electronic,
      hasAnalysis: Boolean(analysisItem),
      pagesKept,
   });
   return overlayAcceptedFieldStatuses(provenanceAfterLocate(previous, {
      aiPagesKept: pagesKept,
      pageFindStatus: pageFind,
      pagesStatus: pageFind === "found"
         ? pageFindConfidenceStatus(analysisItem, pagesKept)
         : undefined,
      analysisStale: options?.analysisStale === true,
   }), row?.fieldAnnotations);
}

export function buildContentProvenanceForAnalysisItem(
   row: any,
   analysisItem: any | undefined,
   options?: { hasCaseDescription?: boolean },
): ANNServitutProvenance {
   const previous = getRowProvenance(row);
   const fieldStatuses = contentFieldStatuses(
      analysisItem,
      isRowFetched(row),
      options?.hasCaseDescription !== false,
   );
   return overlayAcceptedFieldStatuses(provenanceAfterContentAnalysis(previous, {
      resumeStatus: fieldStatuses.resumeStatus,
      entitledPartyStatus: fieldStatuses.entitledPartyStatus,
      conditionStatus: fieldStatuses.conditionStatus,
      statementStatus: fieldStatuses.statementStatus,
   }), row?.fieldAnnotations);
}
